import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Plus, Trash2, ChevronRight, ShieldAlert, Pencil, Check, X as XIcon, ChevronsUpDown } from "lucide-react";

/* Searchable combobox for picking a team member from a potentially large list. */
function TeamPicker({ team, value, onValueChange, placeholder = "Auto-route (least busy)", className = "" }) {
  const [open, setOpen] = useState(false);
  const selected = (team || []).find((t) => t.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className={`w-full justify-between font-normal text-sm h-9 ${className}`}>
          {selected
            ? <span className="truncate">{selected.name}</span>
            : <span className="text-muted-foreground truncate">{placeholder}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start">
        <Command>
          <CommandInput placeholder="Search by name…" className="h-9" />
          <CommandList>
            <CommandEmpty>No match found.</CommandEmpty>
            <CommandItem value="__auto__" onSelect={() => { onValueChange(""); setOpen(false); }}>
              <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} />
              Auto-route (least busy)
            </CommandItem>
            {(team || []).map((t) => (
              <CommandItem key={t.id} value={t.name} onSelect={() => { onValueChange(t.id); setOpen(false); }}>
                <Check className={`mr-2 h-4 w-4 ${value === t.id ? "opacity-100" : "opacity-0"}`} />
                {t.name}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* One escalation level: pick ANY institute user OR type an email/phone. */
function LevelRow({ lv, allUsers, onChange, onRemove }) {
  const set = (k) => (e) => onChange({ ...lv, [k]: e?.target ? e.target.value : e });

  // When a user is picked from the dropdown, auto-fill email, phone and name label
  // from the user record. Manual fields remain editable after auto-fill.
  const handlePersonSelect = (userId) => {
    if (!userId) {
      // Cleared — wipe auto-filled fields so the row resets
      onChange({ ...lv, notify_id: "", notify_email: "", notify_phone: "", label: "" });
      return;
    }
    const user = (allUsers || []).find((u) => u.id === userId);
    onChange({
      ...lv,
      notify_id:    userId,
      notify_email: user?.email || lv.notify_email || "",
      notify_phone: user?.phone || lv.notify_phone || "",
      label:        user?.name  || lv.label        || "",
    });
  };

  return (
    <div className="rounded-xl border p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold">Level {lv.level}</span>
        <button type="button" onClick={onRemove} className="text-xs text-red-600 hover:underline">Remove</button>
      </div>
      <div>
        <Label className="text-xs">Person (optional — any institute user)</Label>
        <TeamPicker
          team={allUsers}
          value={lv.notify_id || ""}
          onValueChange={handlePersonSelect}
          placeholder="Not a system user"
          className="mt-1"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Email {lv.notify_id ? "(optional)" : "(required)"}</Label>
          <Input type="email" value={lv.notify_email || ""} onChange={set("notify_email")}
            placeholder="name@institute.edu" className="mt-1 h-9" />
        </div>
        <div>
          <Label className="text-xs">Contact number</Label>
          <Input value={lv.notify_phone || ""} onChange={set("notify_phone")} placeholder="+91…" className="mt-1 h-9" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Name / office label</Label>
          <Input value={lv.label || ""} onChange={set("label")} placeholder="COE Office" className="mt-1 h-9" />
        </div>
        <div>
          <Label className="text-xs">Escalate after (hours)</Label>
          <Input type="number" min={1} value={lv.after_hours} onChange={set("after_hours")} className="mt-1 h-9" />
        </div>
      </div>
    </div>
  );
}

export default function SubCategoryDialog({ cat, team, trigger, onChanged }) {
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const [sel, setSel] = useState(null);           // sub-category currently being configured
  const [levels, setLevels] = useState([]);
  const [busy, setBusy] = useState(false);
  const [allUsers, setAllUsers] = useState([]);   // all institute users — for escalation person picker

  // Load all institute users once when dialog opens (for escalation person pickers)
  useEffect(() => {
    if (!open) return;
    api.users({ user_type: 'staff', limit: 1000 }).then((r) => setAllUsers(r?.users || [])).catch(() => {});
  }, [open]);
  const [nf, setNf] = useState({ name: "", description: "", sla_hours: "", assignee_id: "" });

  const [notice, setNotice] = useState("");
  const [editingId, setEditingId] = useState(null); // sub-category id currently being edited
  const [ef, setEf] = useState({ name: "", description: "", sla_hours: "" });
  const [editBusy, setEditBusy] = useState(false);

  const startEdit = (sc) => {
    setEditingId(sc.id);
    setEf({ name: sc.name || "", description: sc.description || "", sla_hours: sc.sla_hours ? String(sc.sla_hours) : "" });
  };
  const cancelEdit = () => setEditingId(null);
  const saveEdit = async (sc) => {
    if (!ef.name.trim()) return toast.error("Name is required");
    setEditBusy(true);
    try {
      await api.updateSubCategory(sc.id, {
        name: ef.name.trim(),
        description: ef.description.trim() || null,
        sla_hours: ef.sla_hours ? Number(ef.sla_hours) : null,
      });
      toast.success("Sub-category updated");
      setEditingId(null);
      loadSubs(); onChanged?.();
    } catch (e) { toast.error(e.message || "Could not save changes"); }
    finally { setEditBusy(false); }
  };

  const loadSubs = useCallback(async () => {
    try {
      const r = await api.subCategories({ category_id: cat.id, all: 1 });
      setSubs(r.sub_categories || []);
      setNotice(r.unavailable ? (r.note || "Sub-categories are not set up on this database yet.") : "");
    } catch (e) { toast.error(e.message || "Could not load sub-categories"); }
  }, [cat.id]);

  useEffect(() => { if (open) loadSubs(); }, [open, loadSubs]);

  // load the escalation chain for whatever is selected (null = category-level default)
  useEffect(() => {
    if (!open) return;
    api.escalation(cat.id, sel?.id)
      .then((r) => setLevels((r.levels || []).map((l) => ({
        level: l.level, notify_id: l.notify_id || "", notify_email: l.notify_email || "",
        notify_phone: l.notify_phone || "", label: l.label || "", after_hours: l.after_hours || 24,
      }))))
      .catch(() => setLevels([]));
  }, [open, cat.id, sel]);

  const addSub = async () => {
    if (!nf.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      await api.createSubCategory({
        category_id: cat.id, name: nf.name.trim(),
        description: nf.description.trim() || undefined,
        sla_hours: nf.sla_hours ? Number(nf.sla_hours) : undefined,
        assignee_id: nf.assignee_id || undefined,
      });
      toast.success("Sub-category added — users can raise tickets under it now");
      setNf({ name: "", description: "", sla_hours: "", assignee_id: "" });
      loadSubs(); onChanged?.();
    } catch (e) { toast.error(e.message || "Could not add"); }
    finally { setBusy(false); }
  };

  const setHandler = async (sc, assignee_id) => {
    try {
      await api.updateSubCategory(sc.id, { assignee_id: assignee_id || null });
      toast.success("Handling technician updated");
      loadSubs(); onChanged?.();
    } catch (e) { toast.error(e.message); }
  };

  const toggleSub = async (sc, active) => {
    try { await api.updateSubCategory(sc.id, { is_active: active }); loadSubs(); onChanged?.(); }
    catch (e) { toast.error(e.message); }
  };

  const removeSub = async (sc) => {
    if (!window.confirm(`Delete "${sc.name}"?`)) return;
    try { await api.deleteSubCategory(sc.id); toast.success("Deleted"); if (sel?.id === sc.id) setSel(null); loadSubs(); onChanged?.(); }
    catch (e) { toast.error(e.message); }
  };

  const addLevel = () => {
    const next = (levels.length ? Math.max(...levels.map((l) => l.level)) : 0) + 1;
    setLevels((s) => [...s, { level: next, notify_id: "", notify_email: "", notify_phone: "", label: "", after_hours: next * 24 }]);
  };

  const saveLevels = async () => {
    for (const l of levels) {
      if (!l.notify_id && !(l.notify_email || "").trim())
        return toast.error(`Level ${l.level} needs a person or an email address.`);
    }
    setBusy(true);
    try {
      await api.setEscalation(cat.id, levels, sel?.id);
      toast.success(sel ? `Escalation saved for "${sel.name}"` : "Default escalation saved for this category");
    } catch (e) { toast.error(e.message || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{cat.name} — sub-categories &amp; escalation</DialogTitle>
        </DialogHeader>

        {notice && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/70 dark:bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-400 mb-3">
            {notice}
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-5">
          {/* LEFT: sub-categories */}
          <div className="space-y-3">
            <div className="overline">Sub-categories</div>
            <p className="text-[11px] text-muted-foreground">
              Once a category has sub-categories, users must pick one when raising a ticket.
              New ones appear for users immediately.
            </p>

            <div className="space-y-2">
              {subs.length === 0 && (
                <div className="text-sm text-muted-foreground">
                  None yet — tickets are raised against the category itself.
                </div>
              )}
              {subs.map((sc) => (
                <div key={sc.id}
                  className={`rounded-xl border p-3 ${sel?.id === sc.id ? "border-cobalt-500 bg-cobalt-500/5" : ""}`}>
                  {editingId === sc.id ? (
                    <div className="space-y-2">
                      <Input value={ef.name} onChange={(e) => setEf((s) => ({ ...s, name: e.target.value }))}
                        placeholder="Name" className="h-8 text-sm" />
                      <Input value={ef.description} onChange={(e) => setEf((s) => ({ ...s, description: e.target.value }))}
                        placeholder="Description (optional)" className="h-8 text-sm" />
                      <Input type="number" min={1} value={ef.sla_hours}
                        onChange={(e) => setEf((s) => ({ ...s, sla_hours: e.target.value }))}
                        placeholder={`SLA hours (blank = inherit ${cat.sla_hours}h)`} className="h-8 text-sm" />
                      <div className="flex gap-2">
                        <Button type="button" size="sm" disabled={editBusy} onClick={() => saveEdit(sc)}
                          className="h-7 rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1 flex-1">
                          <Check className="h-3.5 w-3.5" /> {editBusy ? "Saving…" : "Save"}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={cancelEdit}
                          className="h-7 rounded-full gap-1"><XIcon className="h-3.5 w-3.5" /> Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setSel(sc)} className="flex-1 text-left">
                        <div className="text-sm font-medium flex items-center gap-1.5">
                          {sc.name}
                          <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {sc.sla_hours ? `SLA ${sc.sla_hours}h` : `SLA inherited (${cat.sla_hours}h)`}
                          {sc.is_active === false ? " · inactive" : ""}
                        </div>
                      </button>
                      <Switch checked={sc.is_active !== false} onCheckedChange={(v) => toggleSub(sc, v)} />
                      <button type="button" onClick={() => startEdit(sc)} className="text-muted-foreground hover:text-cobalt-500" title="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => removeSub(sc)} className="text-red-600" title="Delete">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  <div className="mt-2">
                    <Label className="text-[11px]">Handling technician (gets these tickets)</Label>
                    <TeamPicker
                      team={allUsers}
                      value={sc.assignee_id || ""}
                      onValueChange={(v) => setHandler(sc, v)}
                      className="mt-1 h-8 text-xs"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-xl border p-3 space-y-2">
              <div className="text-sm font-semibold">Add sub-category</div>
              <Input value={nf.name} onChange={(e) => setNf((s) => ({ ...s, name: e.target.value }))}
                placeholder="e.g. Revaluation request" className="h-9" />
              <Input value={nf.description} onChange={(e) => setNf((s) => ({ ...s, description: e.target.value }))}
                placeholder="Short description (optional)" className="h-9" />
              <Input type="number" min={1} value={nf.sla_hours}
                onChange={(e) => setNf((s) => ({ ...s, sla_hours: e.target.value }))}
                placeholder={`SLA hours (blank = inherit ${cat.sla_hours}h)`} className="h-9" />
              <div>
                <Label className="text-[11px]">Handling technician</Label>
                <TeamPicker
                  team={allUsers}
                  value={nf.assignee_id || ""}
                  onValueChange={(v) => setNf((s) => ({ ...s, assignee_id: v }))}
                  className="mt-1"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Tickets raised under this sub-category go straight to this person.
                </p>
              </div>
              <Button type="button" onClick={addSub} disabled={busy}
                className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5 h-9">
                <Plus className="h-4 w-4" /> Add
              </Button>
            </div>
          </div>

          {/* RIGHT: escalation for the selection */}
          <div className="space-y-3">
            <div className="overline">Escalation chain</div>
            <div className="flex items-center gap-2 text-xs">
              <button type="button" onClick={() => setSel(null)}
                className={`px-3 py-1.5 rounded-full ${!sel ? "bg-zinc-900 text-white dark:bg-white dark:text-zinc-900" : "glass"}`}>
                Category default
              </button>
              {sel && (
                <span className="px-3 py-1.5 rounded-full bg-cobalt-500 text-white">{sel.name}</span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {sel
                ? `Applies to tickets in "${sel.name}". If left empty, the category default is used.`
                : "Used by any sub-category that has no chain of its own."}
            </p>

            {levels.length === 0 && (
              <div className="text-sm text-muted-foreground flex items-start gap-2 rounded-xl border p-3">
                <ShieldAlert className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
                <span>No escalation set. Tickets stay with the assignee and are never escalated.</span>
              </div>
            )}

            {[...levels].sort((a, b) => a.level - b.level).map((lv) => (
              <LevelRow key={lv.level} lv={lv} allUsers={allUsers}
                onChange={(n) => setLevels((s) => s.map((x) => (x.level === lv.level ? n : x)))}
                onRemove={() => setLevels((s) => s.filter((x) => x.level !== lv.level).map((x, i) => ({ ...x, level: i + 1 })))}
              />
            ))}

            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={addLevel} className="rounded-full h-9 flex-1">
                + Add level
              </Button>
              <Button type="button" onClick={saveLevels} disabled={busy}
                className="rounded-full h-9 flex-1 bg-cobalt-500 hover:bg-cobalt-600 text-white">
                {busy ? "Saving…" : "Save chain"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
